import { z } from 'zod'
import i18next from 'i18next'
import Decimal from 'decimal.js'
import { errorContainsInvalidItems, errorParentIsRemoved, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type { Coassign_Project_Insert_Input, Coassign_Project_Set_Input } from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { buildM2MMutations, buildM2MLocalDBquery } from '@/lib/utils/m2mHelper'
import { type RawBuilder, sql } from 'kysely'
import { withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'
import { isWithinInterval, min, max, differenceInDays, formatISO, startOfMonth } from 'date-fns'
import { pick, filterPick } from '@/lib/utils/functional'
import { ref } from 'vue'
import { DEFAULT_DAILY_WORK_SECONDS_PER_MONTH } from '@/lib/dailyWorkTimeConst'
import { type Reactive } from 'vue'
import { isExist, isExistAll } from '@/lib/relation-graph'

function json<T>(value: T): RawBuilder<T> {
  return sql`${JSON.stringify(value)}`
}

const TABLE_NAME = 'project' as const

const dailyWorkTime = ref(DEFAULT_DAILY_WORK_SECONDS_PER_MONTH)

export const setDailyWorkTime = (workTime: number) => {
  dailyWorkTime.value = workTime
}

const isUniqueKey = async ({
  id,
  code
}: {
  id: string
  code?: string | undefined | null
}): Promise<Boolean> => {
  if (code === undefined || code === null) return true
  const isExist = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom(TABLE_NAME)
      .select('code')
      .where('code', '=', code)
      .where('id', '!=', id)
      .execute()
  })
  if (isExist.length > 0) return false
  return true
}

const isValidPeriod = ({
  start_date,
  end_date
}: {
  start_date?: string
  end_date?: string
}): boolean => {
  if (start_date === undefined || end_date === undefined || start_date === '' || end_date === '')
    return true
  const startDateObj = new Date(start_date)
  const endDateObj = new Date(end_date)
  return startDateObj <= endDateObj
}

const isWithinFiveYears = ({
  start_date,
  end_date
}: {
  start_date?: string
  end_date?: string
}): boolean => {
  if (start_date === undefined || end_date === undefined || start_date === '' || end_date === '')
    return true
  const startDateObj = new Date(start_date)
  const endDateObj = new Date(end_date)
  const fiveYearsLater = new Date(startDateObj)
  fiveYearsLater.setFullYear(fiveYearsLater.getFullYear() + 5)

  return endDateObj < fiveYearsLater
}

const isActualWithinPeriod = async ({
  id,
  start_date,
  end_date
}: {
  id: string
  start_date?: string
  end_date?: string
}): Promise<Boolean> => {
  if (start_date === undefined || end_date === undefined) {
    return true
  }
  const workOnPjs = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom('work_on_project')
      .select(['work_on_project.user_id', 'work_on_project.actuals'])
      .where('work_on_project.project_id', '=', id)
      .execute()
  })
  const workActualDates = workOnPjs
    .map((wp) => Object.entries(wp.actuals))
    .flat()
    .filter(([, seconds]) => seconds > 0)
    .map(([date]) => new Date(date))

  const otherCostActuals = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom('target_cost_other_actual')
      .innerJoin(
        'target_cost_other',
        'target_cost_other.id',
        'target_cost_other_actual.target_cost_other_id'
      )
      .select(['target_cost_other_actual.month'])
      .where('target_cost_other.project_id', '=', id)
      .where('target_cost_other_actual.deleted_at', 'is', null)
      .where('target_cost_other.deleted_at', 'is', null)
      .execute()
  })

  // その他費用は月単位でチェック（実績月が開始月〜終了月の範囲内か）
  const projectStartMonth = startOfMonth(new Date(start_date))
  const projectEndMonth = startOfMonth(new Date(end_date))
  const otherCostValid = otherCostActuals.every((oc) => {
    const actualMonth = startOfMonth(new Date(oc.month))
    return actualMonth >= projectStartMonth && actualMonth <= projectEndMonth
  })
  if (!otherCostValid) return false

  // 稼働実績は日単位でチェック
  if (workActualDates.length === 0) return true
  const interval = { start: new Date(start_date), end: new Date(end_date) }
  return (
    isWithinInterval(min(workActualDates), interval) &&
    isWithinInterval(max(workActualDates), interval)
  )
}

export const customFieldSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('STRING'), payload: z.string().trim().min(2).max(128).nullish() }),
  z.object({
    type: z.literal('NUMBER'),
    payload: z.number().int().min(0).max(1000000000).nullish()
  }),
  z.object({
    type: z.literal('DATETIME'),
    payload: z
      .string()
      .regex(/^(19[8-9]\d|20\d\d)-\d{2}-\d{2}$/)
      .nullish()
  }),
  z.object({
    type: z.literal('CHOSE_SINGLE'),
    payload: z
      .string()
      .max(32)
      .refine(isExist('custom_field_choice', 'key'), errorContainsInvalidItems)
      .nullish()
  }),
  z.object({
    type: z.literal('CHOSE_MULTIPLE'),
    payload: z
      .array(z.string().max(32))
      .max(30, { message: i18next.t('stages.labels.error.max30Items') })
      .refine(isExistAll('custom_field_choice', 'key'), errorContainsInvalidItems)
      .nullish()
  }),
  z.object({ type: z.literal('STRING_REQUIRED'), payload: z.string().trim().min(2).max(128) }),
  z.object({
    type: z.literal('NUMBER_REQUIRED'),
    payload: z.number().int().min(0).max(1000000000)
  }),
  z.object({
    type: z.literal('DATETIME_REQUIRED'),
    payload: z.string().regex(/^(19[8-9]\d|20\d\d)-\d{2}-\d{2}$/)
  }),
  z.object({
    type: z.literal('CHOSE_SINGLE_REQUIRED'),
    payload: z.string().refine(isExist('custom_field_choice', 'key'), errorContainsInvalidItems)
  }),
  z.object({
    type: z.literal('CHOSE_MULTIPLE_REQUIRED'),
    payload: z
      .array(z.string())
      .min(1)
      .max(30, { message: i18next.t('stages.labels.error.max30Items') })
      .refine(isExistAll('custom_field_choice', 'key'), errorContainsInvalidItems)
  })
])

export const projectTableSchema = z.object({
  id: z.string().max(21),
  name: z
    .string()
    .trim()
    .min(2)
    .max(128)
    .transform((val) => {
      // 制御文字を削除（バリデーション後）
      // eslint-disable-next-line no-control-regex
      return val.replace(/[\x00-\x1F\x7F]/g, '')
    }),
  code: z
    .string()
    .min(2)
    .max(32)
    .regex(/^[!-~]+$/, { message: i18next.t('stages.labels.error.alphanumeric') })
    .nullish(),
  start_date: z.string().regex(/^(19[8-9]\d|20\d\d)-\d{2}-\d{2}$/),
  end_date: z.string().regex(/^(19[8-9]\d|20\d\d)-\d{2}-\d{2}$/),
  target_sales: z.number().int().min(0).max(10000000000).nullish(),
  target_cost: z.number().int().min(0).max(1000000000).nullish(),
  target_man_hours_in_sec: z.number().nullish(),
  client_id: z.string().max(21).refine(isExist('client'), errorParentIsRemoved).nullish(),
  detail: z.string().max(5000).nullish(),
  custom_fields: z
    .string()
    .default('{}')
    .transform(async (value) => {
      const parsedValue = JSON.parse(value)
      const obj: Record<string, { type: string | undefined; payload: string }> = {}
      const result = await withStage(async (db: Kysely<DB>) => {
        return await db
          .selectFrom('custom_field')
          .select([
            'custom_field.key as key',
            'custom_field.value_type as type',
            'custom_field.visible as visible',
            'custom_field.required as required'
          ])
          .where('custom_field.visible', '=', 1)
          .execute()
      })
      result.forEach((r) => {
        obj[r.key] = {
          type: r.required === 1 ? `${r.type}_REQUIRED` : r.type,
          payload: parsedValue[r.key]
        }
      })
      return obj
    })
    .pipe(z.record(customFieldSchema))
    .transform((value) => {
      const obj: any = {}
      for (const key of Object.keys(value)) {
        obj[key] = value[key].payload
      }
      return obj
    }),
  target_sales_breakdown_plan: z
    .string()
    .default('[]')
    .transform((value) => JSON.parse(value))
    .pipe(
      z.array(
        z.object({
          offset: z.number().min(0),
          sales: z.number().min(0).max(10000000000)
        })
      )
    )
})

const projectTableColumns = Object.keys(projectTableSchema.shape) as Array<
  keyof typeof projectTableSchema.shape
>

const projectRelationSchema = z.object({
  userIds: z
    .array(z.string().max(21))
    .max(30, { message: i18next.t('stages.labels.error.max30Users') })
    .refine(isExistAll('user'), errorContainsInvalidItems)
    .default([]),
  departmentIds: z
    .array(z.string().max(21))
    .max(10, { message: i18next.t('stages.labels.error.max10Departments') })
    .refine(isExistAll('department'), errorContainsInvalidItems)
    .default([])
})

const convenientInputSchema = z.object({
  target_man_month: z.number().min(0).max(9999.999).nullish()
})

export const projectSchema = projectTableSchema
  .extend(projectRelationSchema.shape)
  .extend(convenientInputSchema.shape)
  .refine(isUniqueKey, () => ({
    message: i18next.t('stages.labels.error.codeExists'),
    path: ['code']
  }))
  .refine(isValidPeriod, () => ({
    message: i18next.t('stages.labels.error.endDateAfterStartDate'),
    path: ['end_date']
  }))
  .refine(isWithinFiveYears, () => ({
    message: i18next.t('stages.labels.error.within5Years'),
    path: ['end_date']
  }))
  .refine(isActualWithinPeriod, () => ({
    message: i18next.t('stages.labels.error.workResultOutsidePeriod'),
    path: ['start_date']
  }))

export type Project = z.input<typeof projectSchema>

export class ProjectMutator extends Mutator<typeof projectSchema> {
  static readonly stageableClassName = 'ProjectMutator'
  schema = projectSchema
  table = TABLE_NAME
  indirectTables = ['custom_field_choice' as const]

  // dailyWorkTimeSecondsPerMonth: sec to month
  static async load(id: string): Promise<Reactive<ProjectMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    self.context = { id }
    if (base !== undefined) {
      const serializedBase = {
        ...base,
        custom_fields: base.custom_fields ? JSON.stringify(base.custom_fields) : '{}',

        target_sales_breakdown_plan: JSON.stringify(base.target_sales_breakdown_plan),
        target_sales: base.target_sales != null ? Number(base.target_sales) : null
      }
      self.base = serializedBase
      if (typeof self.base.target_man_hours_in_sec === 'number') {
        // 小数点3桁まで丸めて表示
        self.base.target_man_month = new Decimal(self.base.target_man_hours_in_sec)
          .dividedBy(dailyWorkTime.value)
          .toDecimalPlaces(3)
          .toNumber()
      }
    }
    const users = await this.getUserIds(id)
    if (self.base) self.base.userIds = users.map((record) => record.user_id)
    const departments = await this.getDepartmentIds(id)
    if (self.base) self.base.departmentIds = departments.map((record) => record.department_id)
    return self
  }

  private static async getUserIds(id: string) {
    return DataBase.selectFrom('project_to_user')
      .select(['user_id'])
      .where('project_id', '=', id)
      .execute()
  }

  private static async getDepartmentIds(id: string) {
    return DataBase.selectFrom('project_to_department')
      .select(['department_id'])
      .where('project_id', '=', id)
      .execute()
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const project_id = this.context.id!
    const queries: InsertQuery[] = []
    const tenant_id = useUserStore().userInfo!.tenantId
    const rawData = { ...(await this.parsed)!, ...{ tenant_id } }
    const sqliteParams = {
      ...filterPick(rawData, ...projectTableColumns),
      ...{
        custom_fields: json(
          rawData.custom_fields as Record<string, string | number | string[]> | null
        ),
        target_sales_breakdown_plan: json(rawData.target_sales_breakdown_plan),
        target_sales: rawData.target_sales?.toString() ?? null
      },
      ...{ tenant_id }
    }
    queries.push(DataBase.insertInto(this.table).values(sqliteParams).compile())
    const beforeUserIds = this.base?.userIds || []
    const afterUserIds = rawData.userIds
    queries.push(
      ...this.createProjectToUserLocalDBQuery(project_id, beforeUserIds, afterUserIds, tenant_id)
    )
    const beforeDepartmentIds = this.base?.departmentIds || []
    const afterDepartmentIds = rawData.departmentIds
    queries.push(
      ...this.createProjectToDepartmentLocalDBQuery(
        project_id,
        beforeDepartmentIds,
        afterDepartmentIds,
        tenant_id
      )
    )
    return queries
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const project_id = this.context.id!
    const queries: UpdateQuery[] = []
    const tenant_id = useUserStore().userInfo!.tenantId
    const rawData = { ...(await this.parsed)! }
    const modifiedParams = pick(
      rawData,
      Object.keys(this.modified) as (keyof typeof this.modified)[]
    )
    const sqliteParams = {
      ...filterPick(modifiedParams, ...projectTableColumns),
      ...{
        custom_fields: json(
          rawData.custom_fields as Record<string, string | number | string[]> | null
        ),
        target_sales_breakdown_plan: json(rawData.target_sales_breakdown_plan),
        target_sales: rawData.target_sales?.toString() ?? null
      }
    }
    queries.push(
      DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', project_id).compile()
    )
    const beforeUserIds = this.base?.userIds || []
    const afterUserIds = rawData.userIds
    queries.push(
      ...this.createProjectToUserLocalDBQuery(project_id, beforeUserIds, afterUserIds, tenant_id)
    )
    const beforeDepartmentIds = this.base?.departmentIds || []
    const afterDepartmentIds = rawData.departmentIds
    queries.push(
      ...this.createProjectToDepartmentLocalDBQuery(
        project_id,
        beforeDepartmentIds,
        afterDepartmentIds,
        tenant_id
      )
    )

    const max_offset = differenceInDays(new Date(rawData.end_date), new Date(rawData.start_date))

    // Delete plan.deleted_at where start_offset bigger than max_offset
    queries.push(
      DataBase.deleteFrom('assign_plan')
        .where(({ exists, selectFrom }) =>
          exists(
            selectFrom('assign')
              .select('assign.id')
              .where('assign.project_id', '=', rawData.id)
              .whereRef('assign.id', '=', 'assign_plan.assign_id')
          )
        )
        .where('start_offset', '>', max_offset)
        .compile()
    )
    queries.push(
      DataBase.deleteFrom('volumed_assign_plan')
        .where(({ exists, selectFrom }) =>
          exists(
            selectFrom('volumed_assign')
              .select('volumed_assign.id')
              .where('volumed_assign.project_id', '=', rawData.id)
              .whereRef('volumed_assign.id', '=', 'volumed_assign_plan.volumed_assign_id')
          )
        )
        .where('start_offset', '>', max_offset)
        .compile()
    )

    // Update plan.end_offset where end_offset bigger than max_offset.
    queries.push(
      DataBase.updateTable('assign_plan')
        .set({ end_offset: max_offset })
        .where(({ exists, selectFrom }) =>
          exists(
            selectFrom('assign')
              .select('assign.id')
              .where('assign.project_id', '=', rawData.id)
              .whereRef('assign.id', '=', 'assign_plan.assign_id')
          )
        )
        .where('end_offset', '>', max_offset)
        .where('start_offset', '<=', max_offset)
        .compile()
    )
    queries.push(
      DataBase.updateTable('volumed_assign_plan')
        .set({ end_offset: max_offset })
        .where(({ exists, selectFrom }) =>
          exists(
            selectFrom('volumed_assign')
              .select('volumed_assign.id')
              .where('volumed_assign.project_id', '=', rawData.id)
              .whereRef('volumed_assign.id', '=', 'volumed_assign_plan.volumed_assign_id')
          )
        )
        .where('end_offset', '>', max_offset)
        .where('start_offset', '<=', max_offset)
        .compile()
    )

    return queries
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const id = this.context.id!
    const assignDelete = DataBase.deleteFrom('assign').where('project_id', '=', id).compile()
    const assignPlanDelete = DataBase.deleteFrom('assign_plan')
      .where(
        'assign_id',
        'in',
        DataBase.selectFrom('assign').select('id').where('assign.project_id', '=', id)
      )
      .compile()
    const volumedAssignDelete = DataBase.deleteFrom('volumed_assign')
      .where('project_id', '=', id)
      .compile()
    const volumedAssignToJobSkillDelete = DataBase.deleteFrom('volumed_assign_to_job_skill')
      .where(
        'volumed_assign_id',
        'in',
        DataBase.selectFrom('volumed_assign')
          .select('id')
          .where('volumed_assign.project_id', '=', id)
      )
      .compile()
    const volumedAssignToDepartmentDelete = DataBase.deleteFrom('volumed_assign_to_department')
      .where(
        'volumed_assign_id',
        'in',
        DataBase.selectFrom('volumed_assign')
          .select('id')
          .where('volumed_assign.project_id', '=', id)
      )
      .compile()
    const volumedAssignPlanDelete = DataBase.deleteFrom('volumed_assign_plan')
      .where(
        'volumed_assign_id',
        'in',
        DataBase.selectFrom('volumed_assign')
          .select('id')
          .where('volumed_assign.project_id', '=', id)
      )
      .compile()
    const projectToDepartmentDelete = DataBase.deleteFrom('project_to_department')
      .where('project_id', '=', id)
      .compile()
    const projectToUserDelete = DataBase.deleteFrom('project_to_user')
      .where('project_id', '=', id)
      .compile()
    const targetCostOtherDelete = DataBase.deleteFrom('target_cost_other')
      .where('project_id', '=', id)
      .compile()

    return [
      assignPlanDelete,
      assignDelete,
      volumedAssignPlanDelete,
      volumedAssignToJobSkillDelete,
      volumedAssignToDepartmentDelete,
      volumedAssignDelete,
      projectToDepartmentDelete,
      projectToUserDelete,
      targetCostOtherDelete,
      DataBase.deleteFrom(this.table).where('id', '=', id).compile()
    ]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const mutations: MutationOperationAndVariables[] = []
    const projectId = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const projectParams = { ...filterPick(rawData, ...projectTableColumns) }
    const variables: Coassign_Project_Insert_Input = { ...projectParams, ...{ commit_id } }
    mutations.push({ mutation: insertProjectMutationDocument, variables: { input: variables } })
    mutations.push(
      ...this.createProjectToUserMutations(
        projectId,
        this.base?.userIds || [],
        rawData.userIds,
        commit_id
      )
    )
    mutations.push(
      ...this.createProjectToDeparmentMutations(
        projectId,
        this.base?.departmentIds || [],
        rawData.departmentIds,
        commit_id
      )
    )

    return mutations
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const mutations: MutationOperationAndVariables[] = []
    const projectId = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const modifiedParams = pick(
      rawData,
      Object.keys(this.modified) as (keyof typeof this.modified)[]
    )
    const projectParams = { ...filterPick(modifiedParams, ...projectTableColumns) }
    const variables: Coassign_Project_Set_Input = { ...projectParams, ...{ commit_id } }
    mutations.push({
      mutation: updateProjectMutationDocument,
      variables: {
        id: projectId,
        input: variables
      }
    })
    // プロジェクト期間変更に伴う assign_plan, volumed_assign_plan の切り詰めはDB側で実施
    mutations.push(
      ...this.createProjectToUserMutations(
        projectId,
        this.base?.userIds || [],
        rawData.userIds,
        commit_id
      )
    )
    mutations.push(
      ...this.createProjectToDeparmentMutations(
        projectId,
        this.base?.departmentIds || [],
        rawData.departmentIds,
        commit_id
      )
    )
    return mutations
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const deleted_at = formatISO(new Date())
    return [
      {
        mutation: updateDeletedAtWorkOnProjectMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtProjectToDepartmentMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtProjectToUserMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtAssignPlanMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtAssignMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtVolumedAssignPlanMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtVolumedAssignToJobSkillMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: deleteVolumedAssignToDepartmentByProjectMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtVolumedAssignMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtTargetCostOtherByProjectMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtMutationDocument,
        variables: { project_id: id, deletedAt: deleted_at, commit_id: commitId }
      }
    ]
  }

  private createProjectToUserMutations(
    projectId: string,
    beforeUserIds: string[],
    afterUserIds: string[],
    commitId: string
  ): MutationOperationAndVariables[] {
    return buildM2MMutations({
      fromColumn: 'project_id',
      fromId: projectId,
      toColumn: 'user_id',
      beforeToIds: beforeUserIds,
      afterToIds: afterUserIds,
      insertQuery: insertProjectToUserMutationDocument,
      updateQuery: updateProjectToUserMutationDocument,
      commitId: commitId
    })
  }

  private createProjectToDeparmentMutations(
    projectId: string,
    beforeDepartmentIds: string[],
    afterDepartmentIds: string[],
    commitId: string
  ): MutationOperationAndVariables[] {
    return buildM2MMutations({
      fromColumn: 'project_id',
      fromId: projectId,
      toColumn: 'department_id',
      beforeToIds: beforeDepartmentIds,
      afterToIds: afterDepartmentIds,
      insertQuery: insertProjectToDepartmentMutationDocument,
      updateQuery: updateProjectToDepartmentMutationDocument,
      commitId: commitId
    })
  }

  private createProjectToUserLocalDBQuery(
    projectId: string,
    beforeUserIds: string[],
    afterUserIds: string[],
    tenantId: string
  ) {
    return buildM2MLocalDBquery({
      mappingTable: 'project_to_user',
      fromColumn: 'project_id',
      fromId: projectId,
      toColumn: 'user_id',
      beforeToIds: beforeUserIds,
      afterToIds: afterUserIds,
      tenantId: tenantId
    })
  }

  private createProjectToDepartmentLocalDBQuery(
    projectId: string,
    beforeDepartmentIds: string[],
    afterDepartmentIds: string[],
    tenantId: string
  ) {
    return buildM2MLocalDBquery({
      mappingTable: 'project_to_department',
      fromColumn: 'project_id',
      fromId: projectId,
      toColumn: 'department_id',
      beforeToIds: beforeDepartmentIds,
      afterToIds: afterDepartmentIds,
      tenantId: tenantId
    })
  }
}

const insertProjectMutationDocument = graphql(/* GraphQL */ `
  mutation insertProject($input: coassign_project_insert_input!) {
    insert_coassign_project_one(object: $input) {
      created_at
    }
  }
`)

const updateProjectMutationDocument = graphql(/* GraphQL */ `
  mutation updateProject($id: String!, $input: coassign_project_set_input!) {
    update_coassign_project_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)

const insertProjectToUserMutationDocument = graphql(/* GraphQL */ `
  mutation insertProjectToUserMutationDocument($input: [coassign_project_to_user_insert_input!]!) {
    insert_coassign_project_to_user(objects: $input) {
      affected_rows
    }
  }
`)

const updateProjectToUserMutationDocument = graphql(/* GraphQL */ `
  mutation updateProjectToUserMutationDocument(
    $project_id: String!
    $user_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_project_to_user(
      where: {
        project_id: { _eq: $project_id }
        user_id: { _eq: $user_id }
        deleted_at: { _is_null: true }
      }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const insertProjectToDepartmentMutationDocument = graphql(/* GraphQL */ `
  mutation insertProjectToDepartmentMutationDocument(
    $input: [coassign_project_to_department_insert_input!]!
  ) {
    insert_coassign_project_to_department(objects: $input) {
      affected_rows
    }
  }
`)

const updateProjectToDepartmentMutationDocument = graphql(/* GraphQL */ `
  mutation updateProjectToDepartmentMutationDocument(
    $project_id: String!
    $department_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_project_to_department(
      where: {
        project_id: { _eq: $project_id }
        department_id: { _eq: $department_id }
        deleted_at: { _is_null: true }
      }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtProjectToDepartmentMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtProjectToDepartmentMutationDocument(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_project_to_department(
      where: { project_id: { _eq: $project_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtProjectToUserMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtProjectToUserMutationDocument(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_project_to_user(
      where: { project_id: { _eq: $project_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtAssignPlanMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtAssignPlanMutationDocument(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_assign_plan(
      where: {
        assign_plan_assign: { project_id: { _eq: $project_id } }
        deleted_at: { _is_null: true }
      }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtAssignMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtAssignMutationDocument(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_assign(
      where: { project_id: { _eq: $project_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtVolumedAssignPlanMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtVolumedAssignPlanMutationDocument(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign_plan(
      where: {
        volumed_assign_plan_assign: { project_id: { _eq: $project_id } }
        deleted_at: { _is_null: true }
      }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtVolumedAssignToJobSkillMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtVolumedAssignToJobSkillMutationDocument(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign_to_job_skill(
      where: {
        volumed_assign: { project_id: { _eq: $project_id } }
        deleted_at: { _is_null: true }
      }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtVolumedAssignMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtVolumedAssignMutationDocument(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign(
      where: { project_id: { _eq: $project_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const deleteVolumedAssignToDepartmentByProjectMutationDocument = graphql(/* GraphQL */ `
  mutation deleteVolumedAssignToDepartmentByProject(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign_to_department(
      where: {
        volumed_assign: { project_id: { _eq: $project_id } }
        deleted_at: { _is_null: true }
      }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtWorkOnProjectMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtWorkOnProjectMutationDocument(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_work_on_project(
      where: { project_id: { _eq: $project_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtTargetCostOtherByProjectMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtTargetCostOtherByProject(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_target_cost_other(
      where: { project_id: { _eq: $project_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtMutationDocument(
    $project_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_project(
      where: { id: { _eq: $project_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)
